// client/src/components/admin/AdminCourts.js
import React, { useEffect, useState, useCallback } from 'react'
import PageLayout from '../common/layout/PageLayout'
import ApiCourts from '../../api/ApiCourts'
import toastr from 'toastr'
import { formatDateTime } from '../../utils/format'
import ApiUsers from '../../api/ApiUsers'
import CourtFormModal from './CourtFormModal'
import DeleteCourtModal from './DeleteCourtModal'

export default function AdminCourts() {
  const [pending, setPending] = useState([])
  const [approved, setApproved] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(new Set())
  const [userMap, setUserMap] = useState({})
  const [showFormModal, setShowFormModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingCourt, setEditingCourt] = useState(null)
  const [deletingCourt, setDeletingCourt] = useState(null)
  const [formProcessing, setFormProcessing] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [pendingData, recentData] = await Promise.all([
        ApiCourts.search({ verification_status: 'pending', limit: 50 }),
        ApiCourts.search({ limit: 50 }),
      ])
      setPending(Array.isArray(pendingData) ? pendingData : [])
      const recentApproved = (
        Array.isArray(recentData) ? recentData : []
      ).filter((c) => c.verification_status === 'approved')
      setApproved(recentApproved)

      // Load approver names for non-auto approvals
      const approverIds = Array.from(
        new Set(
          recentApproved
            .filter((c) => c.verification_metadata?.reason !== 'mvp-auto')
            .map((c) => c.updated_by_user_id)
            .filter(Boolean),
        ),
      )
      const newUserMap = {}
      for (const id of approverIds) {
        try {
          const u = await ApiUsers.getUserById(id)
          const name =
            u?.firstname || u?.lastname
              ? `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
              : u?.email || `User ${id}`
          newUserMap[id] = name
        } catch {
          newUserMap[id] = `User ${id}`
        }
      }
      if (Object.keys(newUserMap).length > 0) {
        setUserMap((prev) => ({ ...prev, ...newUserMap }))
      }
    } catch (e) {
      console.error(e)
      toastr.error('Failed to load courts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const act = async (id, status) => {
    try {
      setProcessing((s) => new Set(s).add(id))
      await ApiCourts.verify({ id, verification_status: status })
      toastr.success(`Court ${status}`)
      await load()
    } catch (e) {
      toastr.error(e?.message || 'Action failed')
    } finally {
      setProcessing((s) => {
        const n = new Set(s)
        n.delete(id)
        return n
      })
    }
  }

  const handleAddCourt = () => {
    setEditingCourt(null)
    setShowFormModal(true)
  }

  const handleEditCourt = (court) => {
    setEditingCourt(court)
    setShowFormModal(true)
  }

  const handleDeleteCourt = (court) => {
    setDeletingCourt(court)
    setShowDeleteModal(true)
  }

  const handleFormSave = async (courtData) => {
    try {
      setFormProcessing(true)
      if (editingCourt) {
        // Update existing court
        await ApiCourts.update(courtData)
        toastr.success('Court updated successfully')
      } else {
        // Create new court
        await ApiCourts.create(courtData)
        toastr.success('Court added successfully')
      }
      setShowFormModal(false)
      setEditingCourt(null)
      await load()
    } catch (e) {
      toastr.error(e?.message || 'Failed to save court')
    } finally {
      setFormProcessing(false)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      setFormProcessing(true)
      await ApiCourts.delete(deletingCourt.id)
      toastr.success('Court deleted successfully')
      setShowDeleteModal(false)
      setDeletingCourt(null)
      await load()
    } catch (e) {
      toastr.error(
        e?.message || 'Failed to delete court. It may be in use by players.',
      )
    } finally {
      setFormProcessing(false)
    }
  }

  const handleFormCancel = () => {
    setShowFormModal(false)
    setEditingCourt(null)
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
    setDeletingCourt(null)
  }

  return (
    <PageLayout title="Admin: Courts">
      <div className="container">
        {loading && <div>Loading…</div>}

        {!loading && (
          <>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
              <h5 className="mb-0">Pending Courts</h5>
            </div>
            {pending.length === 0 ? (
              <div className="alert alert-success">No pending courts</div>
            ) : (
              <div className="table-responsive mb-4">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Address</th>
                      <th>Surface</th>
                      <th>Lighting</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td className="small text-muted">
                          {[c.address_line1, c.city, c.state, c.zip]
                            .filter(Boolean)
                            .join(', ')}
                        </td>
                        <td>{c.surface || '—'}</td>
                        <td>{c.lighting ? 'Yes' : 'No'}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-success"
                              disabled={processing.has(c.id)}
                              onClick={() => act(c.id, 'approved')}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              disabled={processing.has(c.id)}
                              onClick={() => act(c.id, 'rejected')}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Recently Approved Courts</h5>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddCourt}
              >
                Add Court
              </button>
            </div>
            {approved.length === 0 ? (
              <div className="alert alert-light">No recent approvals</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Address</th>
                      <th>Approved</th>
                      <th>By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approved.map((c) => {
                      const approvedAt = c.updated_at || c.created_at
                      const auto =
                        c.verification_metadata?.reason === 'mvp-auto'
                      const by = auto
                        ? 'Auto-approved'
                        : c.updated_by_user_id
                          ? userMap[c.updated_by_user_id] ||
                            `User ${c.updated_by_user_id}`
                          : '—'
                      return (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td className="small text-muted">
                            {[c.address_line1, c.city, c.state, c.zip]
                              .filter(Boolean)
                              .join(', ')}
                          </td>
                          <td className="small">
                            {approvedAt
                              ? formatDateTime(
                                  typeof approvedAt === 'string'
                                    ? approvedAt
                                    : approvedAt.toISOString(),
                                )
                              : '—'}
                          </td>
                          <td className="small">{by}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleEditCourt(c)}
                                title="Edit court"
                                aria-label={`Edit ${c.name}`}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteCourt(c)}
                                title="Delete court"
                                aria-label={`Delete ${c.name}`}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <CourtFormModal
        show={showFormModal}
        onHide={handleFormCancel}
        onSave={handleFormSave}
        court={editingCourt}
        isProcessing={formProcessing}
      />

      <DeleteCourtModal
        show={showDeleteModal}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        court={deletingCourt}
        isProcessing={formProcessing}
      />
    </PageLayout>
  )
}
